import { useState, useCallback } from 'react';

interface ContactMessage {
  name: string;
  email: string;
  organization: string;
  message: string;
  subject: string;
  startedAt?: number;
  website?: string;
}

const CONTACT_API_URL =
  `${process.env.REACT_APP_CONTACT_API_URL}/contact` ||
  'https://5kdsze9xs4.execute-api.eu-central-1.amazonaws.com/contact';
const ACADEMIC_API_URL =
  process.env.REACT_APP_ACADEMIC_API_URL || 'https://o0vnudbmyc.execute-api.eu-central-1.amazonaws.com';
const REQUEST_PRICING_SUBJECT = 'Request pricing';
const REQUEST_PRICING_TAG_ID = '6874';

const shouldSkipActiveCampaignForAcademicEmail = (email: string) => {
  const normalizedEmail = email.toLowerCase();
  return normalizedEmail.includes('.edu.') || normalizedEmail.includes('.ac.');
};

const splitFullName = (fullName: string) => {
  const trimmedName = fullName.trim();
  if (!trimmedName) {
    return { firstName: '', lastName: '' };
  }

  const [firstName, ...lastNameParts] = trimmedName.split(/\s+/);
  return {
    firstName,
    lastName: lastNameParts.join(' '),
  };
};

export const useContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSentMessage, setEmailSentMessage] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const submitMessage = useCallback(async (messageInfo: ContactMessage) => {
    setIsSubmitting(true);
    setFormError(null);
    setEmailSentMessage('');

    try {
      const isRequestPricing = messageInfo.subject === REQUEST_PRICING_SUBJECT;
      const shouldSkipActiveCampaign =
        isRequestPricing && shouldSkipActiveCampaignForAcademicEmail(messageInfo.email);

      if (isRequestPricing && !shouldSkipActiveCampaign) {
        const { firstName, lastName } = splitFullName(messageInfo.name);

        try {
          await fetch(`${ACADEMIC_API_URL}/add-pricing-tag`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: messageInfo.email,
              tagId: REQUEST_PRICING_TAG_ID,
              firstName,
              lastName,
              organization: messageInfo.organization,
              createIfNotExists: true,
            }),
          });
        } catch (error) {
          console.error('ActiveCampaign pricing tag error:', error);
        }
      }

      const response = await fetch(CONTACT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageInfo),
      });

      if (!response.ok) throw new Error('Network error');

      setEmailSentMessage('Your email was successfully sent!');
      return true;
    } catch (error) {
      console.error('Email submission error:', error);
      setEmailSentMessage(
        'There was an error with email submission. Please try again or contact us by email at info@disgenet.com',
      );
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const resetForm = useCallback(() => {
    setEmailSentMessage('');
    setFormError(null);
  }, []);

  return {
    isSubmitting,
    emailSentMessage,
    formError,
    setFormError,
    submitMessage,
    resetForm,
  };
};
